import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP } from '../utils/xp';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadGamePool, pickGameWords } from '../data/gameWords';
import { buildCrossword } from '../data/crosswordLayout';
import './Crossword.css';

const TEXT = {
    title: { en: 'Crossword', it: 'Cruciverba', es: 'Crucigrama', fr: 'Mots croisés', pt: 'Palavras cruzadas' },
    building: { en: 'Building your crossword...', it: 'Sto preparando il cruciverba...', es: 'Preparando tu crucigrama...', fr: 'Préparation de votre grille...', pt: 'Preparando suas palavras cruzadas...' },
    notEnough: { en: 'Not enough words yet', it: 'Non ci sono ancora abbastanza parole', es: 'Aún no hay suficientes palabras', fr: 'Pas encore assez de mots', pt: 'Ainda não há palavras suficientes' },
    notEnoughHint: {
        en: 'Save a few words with meanings to your Word Bank and this game will use them.',
        it: 'Salva qualche parola con il significato nel tuo Word Bank e il gioco le userà.',
        es: 'Guarda algunas palabras con su significado en tu Word Bank y el juego las usará.',
        fr: 'Enregistrez quelques mots avec leur sens dans votre Word Bank et le jeu les utilisera.',
        pt: 'Salve algumas palavras com o significado no seu Word Bank e o jogo vai usá-las.',
    },
    across: { en: 'Across', it: 'Orizzontali', es: 'Horizontales', fr: 'Horizontalement', pt: 'Horizontais' },
    down: { en: 'Down', it: 'Verticali', es: 'Verticales', fr: 'Verticalement', pt: 'Verticais' },
    tapClue: { en: 'Tap a clue or a square to start', it: 'Tocca un indizio o una casella per iniziare', es: 'Toca una pista o una casilla para empezar', fr: 'Touchez un indice ou une case pour commencer', pt: 'Toque numa dica ou numa casa para começar' },
    placeholder: { en: 'Type the answer', it: 'Scrivi la risposta', es: 'Escribe la respuesta', fr: 'Écrivez la réponse', pt: 'Escreva a resposta' },
    check: { en: 'Check', it: 'Verifica', es: 'Comprobar', fr: 'Vérifier', pt: 'Verificar' },
    hint: { en: 'Hint', it: 'Aiuto', es: 'Pista', fr: 'Indice', pt: 'Dica' },
    correct: { en: 'Correct! ✓', it: 'Esatto! ✓', es: '¡Correcto! ✓', fr: 'Correct ! ✓', pt: 'Correto! ✓' },
    tryAgain: { en: 'Not quite, try again', it: 'Non proprio, riprova', es: 'Casi, inténtalo de nuevo', fr: 'Pas tout à fait, réessayez', pt: 'Quase, tente de novo' },
    letters: { en: 'letters', it: 'lettere', es: 'letras', fr: 'lettres', pt: 'letras' },
    solved: { en: 'solved', it: 'risolte', es: 'resueltas', fr: 'résolus', pt: 'resolvidas' },
    done: { en: 'Crossword complete!', it: 'Cruciverba completato!', es: '¡Crucigrama completado!', fr: 'Mots croisés terminés !', pt: 'Palavras cruzadas concluídas!' },
    hintsUsed: { en: 'Hints used', it: 'Aiuti usati', es: 'Pistas usadas', fr: 'Indices utilisés', pt: 'Dicas usadas' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
};

const stripAccents = (w) => String(w || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const onlyLetters = (w) => stripAccents(w).replace(/[^a-z]/g, '');
const cellId = (r, c) => r + ',' + c;

// Words that make a good clue: a single word with a short meaning, 3 to 9 letters
const usable = (item) => {
    if (!item.word || !item.translation || /\s/.test(item.word.trim())) return false;
    const key = stripAccents(item.word.trim());
    if (!/^[a-z]+$/.test(key)) return false;
    return key.length >= 3 && key.length <= 9 && item.translation.length <= 50;
};

function chooseCrossword(gamePool) {
    const picked = pickGameWords(gamePool, 18, usable);
    const seenKeys = new Set();
    const seenClues = new Set();
    const words = [];
    picked.forEach(item => {
        const key = stripAccents(item.word.trim());
        const clue = item.translation.trim().toLowerCase();
        if (seenKeys.has(key) || seenClues.has(clue)) return;
        seenKeys.add(key);
        seenClues.add(clue);
        words.push({ key, word: item.word.trim(), translation: item.translation.trim() });
    });
    const puzzle = buildCrossword(words);
    if (!puzzle || puzzle.entries.length < 4) return null;
    return puzzle;
}

export default function Crossword() {
    const navigate = useNavigate();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();

    const [gamePool, setGamePool] = useState(null);
    const [puzzle, setPuzzle] = useState(null);
    const [notEnough, setNotEnough] = useState(false);
    const [solved, setSolved] = useState([]);
    const [hinted, setHinted] = useState([]);
    const [hintCount, setHintCount] = useState(0);
    const [selected, setSelected] = useState(null);
    const [answer, setAnswer] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [finished, setFinished] = useState(false);
    const [earnedXp, setEarnedXp] = useState(0);
    const inputRef = useRef(null);

    const startPuzzle = (pool) => {
        const built = chooseCrossword(pool);
        if (!built) {
            setNotEnough(true);
            return;
        }
        setNotEnough(false);
        setPuzzle(built);
        setSolved([]);
        setHinted([]);
        setHintCount(0);
        setSelected(null);
        setAnswer('');
        setMessage('');
        setFinished(false);
    };

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
            const loaded = await loadGamePool(user ? user.id : null, targetLang, interfaceLanguage, level);
            setGamePool(loaded);
            const built = chooseCrossword(loaded);
            if (!built) {
                setNotEnough(true);
                return;
            }
            setPuzzle(built);
        };
        init();
    }, [targetLang, activeLevel, interfaceLanguage]);

    const flash = (text, type) => {
        setMessage(text);
        setMessageType(type);
        setTimeout(() => setMessage(''), 1500);
    };

    const entryByKey = (key) => (puzzle ? puzzle.entries.find(e => e.key === key) : null);

    const selectEntry = (key) => {
        setSelected(key);
        setAnswer('');
        setMessage('');
        setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    };

    // Tapping a square picks the unsolved word that passes through it (a second tap switches direction)
    const tapCell = (r, c) => {
        const through = puzzle.entries.filter(e => e.cells.some(([er, ec]) => er === r && ec === c));
        if (through.length === 0) return;
        const open = through.filter(e => !solved.includes(e.key));
        const options = open.length > 0 ? open : through;
        const current = options.findIndex(e => e.key === selected);
        selectEntry(options[(current + 1) % options.length].key);
    };

    const finishPuzzle = (nextSolved, hints) => {
        const xp = Math.max(10, 40 - hints * 5);
        setEarnedXp(xp);
        setFinished(true);
        awardXP(xp, 'crossword');
        return nextSolved;
    };

    const submitAnswer = () => {
        const entry = entryByKey(selected);
        if (!entry || solved.includes(entry.key)) return;
        if (onlyLetters(answer) === entry.key) {
            const nextSolved = [...solved, entry.key];
            setSolved(nextSolved);
            setAnswer('');
            flash(tx('correct'), 'success');
            if (nextSolved.length === puzzle.entries.length) {
                setTimeout(() => finishPuzzle(nextSolved, hintCount), 700);
            } else {
                const nextOpen = puzzle.entries.find(e => !nextSolved.includes(e.key));
                if (nextOpen) setSelected(nextOpen.key);
            }
        } else {
            flash(tx('tryAgain'), 'error');
        }
    };

    const giveHint = () => {
        const entry = entryByKey(selected);
        if (!entry || solved.includes(entry.key)) return;
        const open = entry.cells.findIndex(([r, c]) => !hinted.includes(cellId(r, c)));
        if (open === -1) return;
        const [r, c] = entry.cells[open];
        setHinted(prev => [...prev, cellId(r, c)]);
        setHintCount(n => n + 1);
    };

    const letterAt = (r, c) => {
        const id = cellId(r, c);
        for (const e of puzzle.entries) {
            const idx = e.cells.findIndex(([er, ec]) => er === r && ec === c);
            if (idx === -1) continue;
            if (solved.includes(e.key) || hinted.includes(id)) return e.key[idx].toUpperCase();
        }
        return '';
    };

    const backBar = (
        <div className="cw-top-bar">
            <button className="cw-back" onClick={() => navigate('/games')}>←</button>
            <div className="cw-title">{tx('title')}</div>
            <div style={{ width: 32 }}></div>
        </div>
    );

    if (notEnough) {
        return (
            <div className="cw-screen">
                {backBar}
                <div className="cw-complete">
                    <div className="cw-complete-icon">✏️</div>
                    <div className="cw-complete-title">{tx('notEnough')}</div>
                    <div className="cw-complete-stat">{tx('notEnoughHint')}</div>
                    <button className="cw-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    if (!puzzle) {
        return (
            <div className="cw-screen">
                {backBar}
                <div className="cw-complete">
                    <div className="cw-complete-stat">{tx('building')}</div>
                </div>
            </div>
        );
    }

    if (finished) {
        return (
            <div className="cw-screen">
                {backBar}
                <div className="cw-complete">
                    <div className="cw-complete-icon">🎉</div>
                    <div className="cw-complete-title">{tx('done')}</div>
                    <div className="cw-complete-stat">{tx('hintsUsed')}: {hintCount}</div>
                    <div className="cw-complete-xp">+{earnedXp} XP</div>
                    <div className="cw-review">
                        {puzzle.entries.map(e => (
                            <div key={e.key + e.dir} className="cw-review-row">
                                <span className="cw-review-word">{e.word}</span>
                                <span className="cw-review-translation">{e.translation}</span>
                            </div>
                        ))}
                    </div>
                    <button className="cw-play-again" onClick={() => startPuzzle(gamePool)}>{tx('again')}</button>
                    <button className="cw-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    const selectedEntry = entryByKey(selected);
    const selectedCells = selectedEntry ? new Set(selectedEntry.cells.map(([r, c]) => cellId(r, c))) : new Set();
    const numbers = {};
    puzzle.entries.forEach(e => { numbers[cellId(e.row, e.col)] = e.number; });
    const usedCells = new Set();
    puzzle.entries.forEach(e => e.cells.forEach(([r, c]) => usedCells.add(cellId(r, c))));

    const rows = [];
    for (let r = 0; r < puzzle.height; r++) {
        const cells = [];
        for (let c = 0; c < puzzle.width; c++) {
            const id = cellId(r, c);
            if (!usedCells.has(id)) {
                cells.push(<div key={id} className="cw-cell empty"></div>);
                continue;
            }
            const letter = letterAt(r, c);
            let cls = 'cw-cell';
            if (selectedCells.has(id)) cls += ' selected';
            if (letter) cls += ' filled';
            cells.push(
                <div key={id} className={cls} onClick={() => tapCell(r, c)}>
                    {numbers[id] && <span className="cw-number">{numbers[id]}</span>}
                    {letter}
                </div>
            );
        }
        rows.push(<div key={r} className="cw-row">{cells}</div>);
    }

    const clueList = (dir) => puzzle.entries.filter(e => e.dir === dir).map(e => (
        <div
            key={e.key + e.dir}
            className={`cw-clue ${solved.includes(e.key) ? 'solved' : ''} ${selected === e.key ? 'active' : ''}`}
            onClick={() => selectEntry(e.key)}
        >
            <span className="cw-clue-number">{e.number}</span>
            <span className="cw-clue-text">
                {solved.includes(e.key) ? e.word : e.translation}
                <span className="cw-clue-length"> ({e.key.length} {tx('letters')})</span>
            </span>
        </div>
    ));

    return (
        <div className="cw-screen">
            {backBar}

            <div className="cw-progress">{solved.length} / {puzzle.entries.length} {tx('solved')}</div>

            <div className="cw-grid" style={{ gridTemplateColumns: `repeat(${puzzle.width}, 1fr)`, maxWidth: puzzle.width * 36 }}>
                {rows}
            </div>

            <div className="cw-answer-bar">
                {selectedEntry && !solved.includes(selectedEntry.key) ? (
                    <>
                        <div className="cw-answer-clue">
                            {selectedEntry.number} {tx(selectedEntry.dir)}: {selectedEntry.translation} ({selectedEntry.key.length})
                        </div>
                        <div className="cw-answer-row">
                            <input
                                ref={inputRef}
                                className="cw-input"
                                value={answer}
                                maxLength={selectedEntry.key.length + 2}
                                placeholder={tx('placeholder')}
                                autoComplete="off"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                onChange={e => setAnswer(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') submitAnswer(); }}
                            />
                            <button className="cw-check-btn" onClick={submitAnswer} disabled={!answer}>{tx('check')}</button>
                            <button className="cw-hint-btn" onClick={giveHint}>💡 {tx('hint')}</button>
                        </div>
                    </>
                ) : (
                    <div className="cw-answer-clue muted">{tx('tapClue')}</div>
                )}
                <div className={`cw-message ${messageType}`}>{message}</div>
            </div>

            <div className="cw-clues">
                <div className="cw-clues-label">{tx('across')}</div>
                {clueList('across')}
                <div className="cw-clues-label">{tx('down')}</div>
                {clueList('down')}
            </div>
        </div>
    );
}